import { useCallback } from 'react';
import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { GroupThreadContext } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { useQueryClient } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { useAppScopeSelection } from '@frontend/scope';
import { queryKeys } from './queries';
import { SchemaSMSDraftService } from './service';
import { ListDraftsIO } from './types';

type DraftCacheEntry = ListDraftsIO['output']['drafts'][number];

type DraftListCache = { drafts: Array<DraftCacheEntry> } | undefined;

// Static key prefix for all listDrafts cache queries
const listDraftsBaseKey = [...queryKeys.baseKey, 'listDrafts'];

/**
 * Optimistically patches the listDrafts cache with the new group name and fires a
 * fire-and-forget upsert to the BE so the updated name is persisted for reloads.
 *
 * The upsert includes the group-thread-specific fields (groupName, threadType,
 * participantPhones) that the BE requires to correctly store group draft metadata.
 * We call SchemaSMSDraftService.Upsert directly (bypassing the mutation hook) to
 * avoid the mutation's onSuccess cache-rebuild logic from interfering with the
 * optimistic update.
 */
export const useDraftGroupNameSync = ({
  threadId,
  groupId,
  isGroupThread,
  groupThreadContext,
}: {
  threadId: string;
  groupId: string;
  isGroupThread: boolean;
  groupThreadContext?: GroupThreadContext;
}) => {
  const queryClient = useQueryClient();
  const { selectedOrgId } = useAppScopeSelection();
  const user = getUser();

  const syncDraftGroupName = useCallback(
    (newGroupName: string) => {
      // 1. Optimistically patch the listDrafts cache so the UI reflects the new name immediately.
      queryClient.setQueriesData<DraftListCache>({ queryKey: listDraftsBaseKey, exact: false }, (oldData) => {
        if (!oldData) return undefined;
        return {
          ...oldData,
          drafts: oldData.drafts.map((draft) =>
            draft.threadId === threadId ? { ...draft, groupName: newGroupName } : draft
          ),
        };
      });

      // 2. Re-upsert the existing draft (if any) so the BE stores the updated group name.
      //    We read from the cache to get the current draft body/media/relatedIds and
      //    include the group-thread-specific fields the BE needs.
      if (!user?.userID) return;

      const allDraftCaches = queryClient.getQueriesData<{ drafts: Array<DraftCacheEntry> } | undefined>({
        queryKey: listDraftsBaseKey,
        exact: false,
      });

      for (const [, cacheData] of allDraftCaches) {
        const existingDraft = cacheData?.drafts.find((d) => d.threadId === threadId);
        if (existingDraft?.draft) {
          const participantPhones = (groupThreadContext?.participants ?? [])
            .map((p) => p.phoneNumber)
            .filter((phone): phone is string => !!phone);

          const upsertRequest: Parameters<typeof SchemaSMSDraftService.Upsert>[0] = {
            orgId: selectedOrgId,
            threadId,
            userId: user.userID,
            draft: existingDraft.draft,
            locationId: existingDraft.locationId ?? groupId,
            departmentId: existingDraft.departmentId,
            personPhone: existingDraft.personPhone,
            locationPhone: existingDraft.locationPhone,
            ...(isGroupThread && {
              groupName: newGroupName,
              threadType: ThreadType.THREAD_TYPE_GROUP_MMS,
              participantPhones,
            }),
          };

          SchemaSMSDraftService.Upsert(upsertRequest).catch(() => {
            // Fire-and-forget: non-critical. The optimistic cache update already reflects
            // the new name in the UI. The BE will be consistent on the next full draft
            // upsert triggered by the user typing in the thread.
          });
          break;
        }
      }
    },
    [queryClient, threadId, groupId, selectedOrgId, user, isGroupThread, groupThreadContext]
  );

  return { syncDraftGroupName };
};
